/**
 * `byteSize.ts` — human-readable byte formatting for TRANSFER readouts.
 *
 * Distinct from the single-value formatters that already exist
 * (`AppCacheModel.formatCacheSize`, `useAssetLibrary.formatDocFileSize`, both
 * verbatim ports of Unity's `ConvertToHumanReadableSize`): a progress readout
 * shows two numbers that must be COMPARABLE at a glance, so the unit is
 * chosen once from the total and written once at the end — `5.5 of 492 MB`,
 * not `5.5 MB of 492 MB` and never `5632 KB of 492 MB`.
 *
 * Web-only. The desktop client has no equivalent: Unity's progress views bind
 * a percentage and a `LogText` line that carries a milestone name, never a
 * byte count, because it downloads through `S3Client.CopyStream` and reports
 * stream position as a fraction. The web shows the bytes because a browser
 * tab gives the user no other way to tell a slow link from a hung one.
 */

const K_UNITS: ReadonlyArray<string> = ["B", "KB", "MB", "GB", "TB"];

/**
 * `"5.5 of 492 MB"`, or `""` when there is nothing meaningful to say (no
 * total, or a total of zero — a cache hit or a scene with no downloads).
 * The empty string is what hides the label, so callers can pass raw counters
 * without branching.
 *
 * `loaded` is clamped into `[0, total]`. The two numbers come from different
 * places — the counter sums the chunks the browser hands us, the total is the
 * `Content-Length` S3 reported — so a body served with a `Content-Encoding`
 * would count decompressed bytes against a compressed length and read
 * `510 of 492 MB`. Clamping keeps the label honest in the only direction it
 * can be wrong.
 */
export function formatTransferProgress(loaded: number, total: number): string {
  if (!Number.isFinite(total) || total <= 0) return "";
  const safeLoaded = Number.isFinite(loaded) ? Math.min(Math.max(loaded, 0), total) : 0;

  let divisor = 1;
  let unit = 0;
  while (total / divisor >= 1024 && unit < K_UNITS.length - 1) {
    divisor *= 1024;
    unit++;
  }

  return `${oneDecimal(safeLoaded / divisor)} of ${oneDecimal(total / divisor)} ${K_UNITS[unit]}`;
}

/** Unity's `{0:0.#}` — at most one decimal, trailing `.0` dropped. */
function oneDecimal(value: number): string {
  return String(Math.round(value * 10) / 10);
}
